// ✅ src/pages/OrderManagement/RiskIssuePrint.jsx
// Print-only page (no sidebar/header wrappers)
// Reads data from sessionStorage first, fallback fetch by contractId
//
// Expected sessionStorage keys (either one):
// 1) "RiskIssuePrint_payload"  -> { success, header, rows }
// 2) "RiskIssuePrint_rows"     -> rows[]
//
// Fallback API (if you want):
//   POST https://nissi-india.com/api/riskIssues/printData  body: { contractId }
//
// Route example:
//   /OrderManagement/RiskIssuePrint?contractId=1

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaPrint, FaArrowLeft } from "react-icons/fa";

const API = "https://nissi-india.com/api/riskIssues";

const safeStr = (v) => String(v ?? "").trim();

const fmtDMY = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return safeStr(dt); // already a string in DB
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export default function RiskIssuePrint() {
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const contractId = useMemo(() => {
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      sessionStorage.getItem("contractid") ||
      "";
    const cid = Number(fromUrl || fromSession || 0);
    return Number.isFinite(cid) ? cid : 0;
  }, [qs]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // header info (optional)
  const [header, setHeader] = useState({
    projectName: "",
    customerName: "",
    customerAddress: "",
    contractId: contractId || "",
  });

  const [rows, setRows] = useState([]);

  const loadFromSession = () => {
    try {
      const raw1 = sessionStorage.getItem("RiskIssuePrint_payload");
      if (raw1) {
        const obj = JSON.parse(raw1);
        const hdr = obj?.header || obj?.data?.header || {};
        const list = obj?.rows || obj?.data?.rows || obj?.recordset || obj?.data || [];
        if (hdr && typeof hdr === "object") {
          setHeader((p) => ({
            ...p,
            projectName: safeStr(hdr.projectName || hdr.fld_projectname || p.projectName),
            customerName: safeStr(hdr.customerName || hdr.fld_customername || p.customerName),
            customerAddress: safeStr(hdr.customerAddress || hdr.fld_customeraddress || p.customerAddress),
            contractId: hdr.contractId || hdr.fld_contractid || p.contractId,
          }));
        }
        if (Array.isArray(list)) {
          setRows(
            list.map((r) => ({
              fld_Riskid: r.fld_Riskid ?? r.fld_riskid ?? r.riskId ?? "",
              fld_Risktitle: safeStr(r.fld_Risktitle ?? r.fld_risktitle ?? r.riskTitle),
              fld_Riskissue: safeStr(r.fld_Riskissue ?? r.fld_riskissue ?? r.riskIssue),
              fld_Responsibility: safeStr(r.fld_Responsibility ?? r.fld_responsibility ?? r.responsibility),
              fld_Resolution: safeStr(r.fld_Resolution ?? r.fld_resolution ?? r.resolution),
              Createdon: r.Createdon ?? r.createdon ?? r.createdOn ?? "",
            }))
          );
          return true;
        }
      }

      const raw2 = sessionStorage.getItem("RiskIssuePrint_rows");
      if (raw2) {
        const list = JSON.parse(raw2);
        if (Array.isArray(list)) {
          setRows(
            list.map((r) => ({
              fld_Riskid: r.fld_Riskid ?? r.fld_riskid ?? r.riskId ?? "",
              fld_Risktitle: safeStr(r.fld_Risktitle ?? r.fld_risktitle ?? r.riskTitle),
              fld_Riskissue: safeStr(r.fld_Riskissue ?? r.fld_riskissue ?? r.riskIssue),
              fld_Responsibility: safeStr(r.fld_Responsibility ?? r.fld_responsibility ?? r.responsibility),
              fld_Resolution: safeStr(r.fld_Resolution ?? r.fld_resolution ?? r.resolution),
              Createdon: r.Createdon ?? r.createdon ?? r.createdOn ?? "",
            }))
          );
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  };

  const fetchFromApi = async () => {
    if (!contractId) throw new Error("contractId missing");
    const res = await axios.post(`${API}/printData`, { contractId });

    if (!res.data?.success) {
      throw new Error(res.data?.message || "Print data not available");
    }

    const hdr = res.data?.header || {};
    const list = res.data?.rows || [];

    setHeader((p) => ({
      ...p,
      projectName: safeStr(hdr.projectName || hdr.fld_projectname || p.projectName),
      customerName: safeStr(hdr.customerName || hdr.fld_customername || p.customerName),
      customerAddress: safeStr(hdr.customerAddress || hdr.fld_customeraddress || p.customerAddress),
      contractId: hdr.contractId || hdr.fld_contractid || p.contractId,
    }));

    setRows(
      (Array.isArray(list) ? list : []).map((r) => ({
        fld_Riskid: r.fld_Riskid ?? r.fld_riskid ?? r.riskId ?? "",
        fld_Risktitle: safeStr(r.fld_Risktitle ?? r.fld_risktitle ?? r.riskTitle),
        fld_Riskissue: safeStr(r.fld_Riskissue ?? r.fld_riskissue ?? r.riskIssue),
        fld_Responsibility: safeStr(r.fld_Responsibility ?? r.fld_responsibility ?? r.responsibility),
        fld_Resolution: safeStr(r.fld_Resolution ?? r.fld_resolution ?? r.resolution),
        Createdon: r.Createdon ?? r.createdon ?? r.createdOn ?? "",
      }))
    );
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");

      try {
        const ok = loadFromSession();
        if (!ok) await fetchFromApi();
      } catch (e) {
        setError(e?.message || "Failed to load print data");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  const goBack = () => {
    // close print window if opened in new tab
    try {
      window.close();
    } catch {
      window.history.back();
    }
  };

  const today = useMemo(() => fmtDMY(new Date()), []);

  return (
    <div className="min-h-screen bg-white">
      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4; margin: 10mm; }
        }
        /* keep table header on each page */
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
      `}</style>

      {/* Top actions */}
      <div className="no-print sticky top-0 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Risk & Issues Print {contractId ? `• Contract: ${contractId}` : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={goBack}
              className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
            >
              <FaArrowLeft />
              Back
            </button>
            <button
              onClick={handlePrint}
              className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Company header (same style you used in other print pages) */}
        <div className="border-2 border-black">
          <div className="text-center py-3 border-b border-black">
            <div className="text-lg font-bold text-red-600">Nissi Infotech</div>
            <div className="text-sm">Nissi</div>
            <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
            <div className="text-xs">
              3/42-12, Nagappa colony, kavundampalayam--641030, Tamil Nadu,India
            </div>
            <div className="text-xs">
              Phone No : 9003744644 Email : infraconsoft-implementation@nissiinfotech.in
            </div>
          </div>

          <div className="text-center py-2 border-b border-black font-bold">
            RISK AND ISSUES REPORT
          </div>

          {/* Header info */}
          <div className="px-3 py-2 border-b border-black text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-semibold">Project Name: </span>
                <span>{header.projectName || "-"}</span>
              </div>
              <div className="text-right">
                <span className="font-semibold">Date: </span>
                <span>{today}</span>
              </div>
              <div>
                <span className="font-semibold">Customer Name: </span>
                <span>{header.customerName || "-"}</span>
              </div>
              <div className="text-right">
                <span className="font-semibold">Contract ID: </span>
                <span>{header.contractId || contractId || "-"}</span>
              </div>
              <div className="col-span-2">
                <span className="font-semibold">Customer Address: </span>
                <span>{header.customerAddress || "-"}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-3">
            {loading ? (
              <div className="text-center py-10 text-gray-600">Loading...</div>
            ) : error ? (
              <div className="text-center py-10 text-red-600">{error}</div>
            ) : !rows.length ? (
              <div className="text-center py-10 text-gray-700">No Records Found!</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border border-black text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-black px-2 py-2 w-12 text-center">S.No</th>
                      <th className="border border-black px-2 py-2 text-left w-56">Risk Title</th>
                      <th className="border border-black px-2 py-2 text-left w-64">Risk/Issues</th>
                      <th className="border border-black px-2 py-2 text-left w-56">Responsibility</th>
                      <th className="border border-black px-2 py-2 text-left">Impact / Mitigation / Resolution</th>
                      <th className="border border-black px-2 py-2 text-center w-28">Created On</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={`${r.fld_Riskid}-${idx}`}>
                        <td className="border border-black px-2 py-2 text-center align-top">
                          {idx + 1}
                        </td>
                        <td className="border border-black px-2 py-2 align-top">
                          {r.fld_Risktitle || ""}
                        </td>
                        <td className="border border-black px-2 py-2 align-top">
                          {r.fld_Riskissue || ""}
                        </td>
                        <td className="border border-black px-2 py-2 align-top">
                          {r.fld_Responsibility || ""}
                        </td>
                        <td className="border border-black px-2 py-2 align-top">
                          {r.fld_Resolution || ""}
                        </td>
                        <td className="border border-black px-2 py-2 text-center align-top">
                          {fmtDMY(r.Createdon)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* footer summary */}
                <div className="mt-3 text-sm">
                  <span className="font-semibold">Total Records: </span>
                  <span>{rows.length}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Small note */}
      
      </div>
    </div>
  );
}
